import { IconQuestionMarkOutline } from '@iwoca/icons';
import { GetMcaEstimatedScheduleResponse } from '@iwoca/lapi-client/edge';
import { Accordion, Pill } from '@iwoca/orion';
import cn from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import styles from './CustomiseOffer.module.css';
import { getOriginalPricing } from './getOriginalPricing';
import { useGetWithdrawalSummaryData } from './useGetWithdrawlSummaryData';
import { MoneyNoDecimals } from '../../../ui/moneyNoDecimals';
import { MoneyWithDecimals } from '../../../ui/moneyWithDecimals';
import { Rate } from '../../../ui/rate';

type WithdrawalSummaryProps = {
    accountId: string;
    schedule: GetMcaEstimatedScheduleResponse;
    loading: boolean;
    drawdownAmount: number;
};

export const WithdrawalSummary = ({
    schedule,
    loading,
    drawdownAmount,
    accountId
}: WithdrawalSummaryProps) => {
    const intl = useIntl();
    const { outstandingPrincipalWithFee, otherFees, isTopUpApproved } =
        useGetWithdrawalSummaryData(accountId);
    const summary = schedule?.data.summary;

    const originalFeeAmount = getOriginalPricing({
        numberOfRepayments: schedule?.data.estimated_schedule.repayments.length,
        totalCreditAmount: summary?.total_credit_amount
    });

    // set revised amount for displayed total principal after funding
    const revisedCapitalAmount = outstandingPrincipalWithFee + drawdownAmount;

    // only highlight upfront fee savings > 2.5%, i.e. where the new fee is 97.5% or below the current fee.
    const hasDiscountedPricingDE =
        summary.funding_fee <= 0.975 * originalFeeAmount || isTopUpApproved;

    return (
        <div className={styles.loanSummary}>
            <div className={styles.loanSummaryTitle}>
                <FormattedMessage
                    id="_.rbl_dashboard.approved_offer.loan_summary_title"
                    defaultMessage="Withdrawal summary"
                />
            </div>
            <div
                data-testid="withdrawal-amount-confirmation"
                className={styles.summaryRow}
            >
                <FormattedMessage
                    id="_.rbl_dashboard.approved_offer.withdrawal_amount_confirmation"
                    defaultMessage="You're withdrawing:"
                />
                <MoneyNoDecimals
                    className={cn(styles.summaryAmount, {
                        [styles.loading]: loading
                    })}
                    value={drawdownAmount}
                />
            </div>
            {hasDiscountedPricingDE && (
                <div className={styles.discountPill}>
                    <Pill>
                        <FormattedMessage
                            id="_.rbl_dashboard.discountedOffer"
                            defaultMessage="Our offer for you: You save {amount}"
                            values={{
                                amount: (
                                    <span>
                                        &thinsp;
                                        <MoneyWithDecimals
                                            className={cn({
                                                [styles.loading]: loading
                                            })}
                                            value={
                                                isTopUpApproved
                                                    ? otherFees
                                                    : originalFeeAmount -
                                                      summary.funding_fee
                                            }
                                        />
                                    </span>
                                )
                            }}
                        />
                    </Pill>
                </div>
            )}
            <div className={styles.summaryRow} data-testid="fee-summary">
                <div className={styles.feeColumn}>
                    <div>
                        <FormattedMessage
                            id="_.rbl_dashboard.approved_offer.fee_amount"
                            defaultMessage="Your fixed fee is:"
                        />
                    </div>
                    <div className={styles.feeNoInterestCopy}>
                        <FormattedMessage
                            id="_.rbl_dashboard.approved_offer.no_interest"
                            defaultMessage="(No interest at all)"
                        />
                    </div>
                </div>

                <div
                    data-testid="fee-amount"
                    className={styles.feeAmountColumn}
                >
                    {hasDiscountedPricingDE && (
                        <MoneyWithDecimals
                            className={cn(
                                styles.originalAmountWithStrikethrough,
                                {
                                    [styles.loading]: loading
                                }
                            )}
                            value={
                                isTopUpApproved
                                    ? summary.total_fees
                                    : originalFeeAmount
                            }
                            data-testid="original-amount"
                        />
                    )}
                    <MoneyWithDecimals
                        className={cn(styles.summaryAmount, {
                            [styles.loading]: loading
                        })}
                        value={summary.funding_fee}
                    />

                    <div
                        className={cn(styles.feePercentage, {
                            [styles.loading]: loading
                        })}
                    >
                        <Rate
                            className={styles.feePercentageAmount}
                            value={summary.funding_fee / revisedCapitalAmount}
                        />{' '}
                        <FormattedMessage
                            id="_.rbl_dashboard.approved_offer.fee_proportion_of_principal"
                            defaultMessage="of your principal amount after funding"
                        />
                    </div>
                </div>
            </div>
            {outstandingPrincipalWithFee && (
                <Accordion
                    className={styles.accordion}
                    title={intl.formatMessage({
                        id: '_.rbl_dashboard.approved_offer.how_is_the_fee_calculated',
                        defaultMessage: 'How is the fee calculated?'
                    })}
                    icon={<IconQuestionMarkOutline />}
                    data-testid="fee-accordion"
                >
                    <p className={styles.accordionExplanation}>
                        <FormattedMessage
                            id="_.rbl_dashboard.approved_offer.fee_explanation"
                            defaultMessage="For topups we calculate a fixed fee taking into account the total outstanding principal after funding and the new loan duration. We then discount this by the fee already paid for the unused period of the existing loan."
                        />
                    </p>
                    <div className={styles.accordionRow}>
                        <FormattedMessage
                            id="_.rbl_dashboard.approved_offer.outstanding_principal"
                            defaultMessage="Outstanding principal:"
                        />
                        <MoneyWithDecimals
                            className={cn(styles.accordionAmount, {
                                [styles.loading]: loading
                            })}
                            value={outstandingPrincipalWithFee}
                        />
                    </div>
                    <div className={styles.accordionRow}>
                        <FormattedMessage
                            id="_.rbl_dashboard.approved_offer.aditional_payout"
                            defaultMessage="Additional payout:"
                        />
                        <MoneyWithDecimals
                            className={cn(styles.accordionAmount, {
                                [styles.loading]: loading
                            })}
                            value={drawdownAmount}
                        />
                    </div>
                    <div
                        className={cn(
                            styles.accordionRow,
                            styles.accordionSummary,
                            styles.gap
                        )}
                    >
                        <FormattedMessage
                            id="_.rbl_dashboard.approved_offer.principal_after_funding"
                            defaultMessage="Principal after funding:"
                        />
                        <MoneyWithDecimals
                            className={cn({
                                [styles.loading]: loading
                            })}
                            value={revisedCapitalAmount}
                        />
                    </div>

                    <div className={styles.accordionRow}>
                        <FormattedMessage
                            id="_.rbl_dashboard.approved_offer.fixed_fee_for_new_loan"
                            defaultMessage="Fixed fee for new loan:"
                        />
                        <MoneyWithDecimals
                            className={cn(styles.accordionAmount, {
                                [styles.loading]: loading
                            })}
                            value={summary.total_fees}
                        />
                    </div>

                    <div className={styles.accordionRow}>
                        <FormattedMessage
                            id="_.rbl_dashboard.approved_offer.refund"
                            defaultMessage="Refund from unused period of existing loan:"
                        />
                        <MoneyWithDecimals
                            className={cn(styles.accordionAmount, {
                                [styles.loading]: loading
                            })}
                            value={Math.min(otherFees, summary.total_fees) * -1}
                        />
                    </div>
                    <div
                        className={cn(
                            styles.accordionRow,
                            styles.accordionSummary
                        )}
                    >
                        <FormattedMessage
                            id="_.rbl_dashboard.approved_offer.payable_fee"
                            defaultMessage="Fixed fee still payable:"
                        />
                        <MoneyWithDecimals
                            className={cn({
                                [styles.loading]: loading
                            })}
                            value={summary.funding_fee}
                        />
                    </div>
                </Accordion>
            )}
            <div
                data-testid="total-repayment-amount"
                className={styles.summaryRow}
            >
                <FormattedMessage
                    id="_.rbl_dashboard.approved_offer.total_repayment"
                    defaultMessage="Your total repayment is:"
                />

                <MoneyNoDecimals
                    className={cn(styles.summaryAmount, {
                        [styles.loading]: loading
                    })}
                    value={summary.total_payable}
                />
            </div>
        </div>
    );
};
